<script setup lang="ts">
/* eslint-disable vue/no-undef-components */
import { Amplitude } from '@seede/amplitude';

const requestURL = useRequestURL();
const ogImageUrl = computed(() => `${requestURL.origin}/Veeso_og.jpg`);

useHead({
  title: 'Veeso AI | Turn Raw Content into Deliverable Designs',
  meta: [
    {
      name: 'description',
      content:
        'Turn copy into polished designs in one click. Veeso auto-layouts social posts, slides, and pages—fully editable, fast, and accurate. No design skills needed.',
    },
    {
      name: 'keywords',
      content:
        'AI design, content-first design, social media visuals, SMB marketing, auto layout, visual content tool',
    },
    { property: 'og:title', content: 'Veeso AI | Turn Raw Content into Deliverable Designs' },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: 'https://veeso.ai' },
    {
      property: 'og:description',
      content:
        'Turn copy into polished designs in one click. Veeso auto-layouts social posts, slides, and pages—fully editable, fast, and accurate. No design skills needed.',
    },
    { property: 'og:image', content: ogImageUrl.value },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: '@veesoai' },
    { name: 'twitter:title', content: 'Veeso AI | Turn Raw Content into Deliverable Designs' },
    {
      name: 'twitter:description',
      content:
        'Turn copy into polished designs in one click. Veeso auto-layouts social posts, slides, and pages—fully editable, fast, and accurate. No design skills needed.',
    },
    { name: 'twitter:image', content: ogImageUrl.value },
  ],
  link: [{ rel: 'canonical', href: 'https://veeso.ai' }],
});

const inviteCodeCookie = useCookie('invite_code', {
  maxAge: 60 * 60 * 24 * 7, // 7 days
  path: '/',
});

const utmCookies = {
  source: useCookie('utm_source', { maxAge: 60 * 60 * 24 * 30, path: '/' }),
  medium: useCookie('utm_medium', { maxAge: 60 * 60 * 24 * 30, path: '/' }),
  campaign: useCookie('utm_campaign', { maxAge: 60 * 60 * 24 * 30, path: '/' }),
  term: useCookie('utm_term', { maxAge: 60 * 60 * 24 * 30, path: '/' }),
  content: useCookie('utm_content', { maxAge: 60 * 60 * 24 * 30, path: '/' }),
};

onMounted(() => {
  Amplitude.pageView({
    page_name: 'landing_home',
  });

  const route = useRoute();
  const { ref, utm_source, utm_medium, utm_campaign, utm_term, utm_content } = route.query;

  if (ref) {
    inviteCodeCookie.value = (Array.isArray(ref) ? ref[0] : ref) as string;
  }

  const getQueryVal = (val: any) => {
    if (Array.isArray(val)) return val[0];
    return val;
  };

  if (utm_source) utmCookies.source.value = getQueryVal(utm_source);
  if (utm_medium) utmCookies.medium.value = getQueryVal(utm_medium);
  if (utm_campaign) utmCookies.campaign.value = getQueryVal(utm_campaign);
  if (utm_term) utmCookies.term.value = getQueryVal(utm_term);
  if (utm_content) utmCookies.content.value = getQueryVal(utm_content);

  if (ref || utm_source || utm_medium || utm_campaign || utm_term || utm_content) {
    const scheduleLowPriority = (fn: () => void) => {
      const w = window as any;
      if (typeof w.requestIdleCallback === 'function') {
        w.requestIdleCallback(fn, { timeout: 3000 });
        return;
      }
      setTimeout(fn, 1200);
    };

    scheduleLowPriority(async () => {
      try {
        const { getApiBaseUrl } = await import('@/composables/useApi');
        const apiBase = getApiBaseUrl();
        $fetch(`${apiBase}/api/visit`, {
          method: 'POST',
          body: {
            ref: getQueryVal(ref),
            url: window.location.href,
            utm_source: getQueryVal(utm_source),
            utm_medium: getQueryVal(utm_medium),
            utm_campaign: getQueryVal(utm_campaign),
            utm_term: getQueryVal(utm_term),
            utm_content: getQueryVal(utm_content),
            metadata: {
              referrer: document.referrer,
              screen_width: window.innerWidth,
              screen_height: window.innerHeight,
            },
          },
        }).catch(() => {});
      } catch (e) {
        console.error(e);
      }
    });
  }
});
</script>

<template>
  <div class="min-h-screen relative flex flex-col index-bg">
    <!-- Header -->
    <AppNav active-path="/" />

    <!-- Hero Section -->
    <FeaturesHeroNext />

    <!-- Every Scene Section -->
    <LazyFeaturesEveryScene />

    <!-- Every Step Section -->
    <LazyFeaturesEveryStep />

    <!-- Testimonials Section -->
    <LazyFeaturesTestimonials />

    <!-- FAQ Section -->
    <LazyFeaturesFaq />

    <!-- Roles Section -->
    <LazyFeaturesRoles />

    <!-- Footer -->
    <LazyAppFooter />
  </div>
</template>

<style scoped>
.index-bg {
  position: relative;
}

.index-bg::before {
  content: '';
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
}

.index-bg > :not(header) {
  position: relative;
  z-index: 1;
}
</style>
